import React from "react";
import { ClientDetailedLinkInviteScreenContent } from "./ClientDetailedLinkInviteScreenContent";
import {
  ProcessInviteCodeDto,
  ValidatedInviteCodeDto,
} from "@growth-machine-llc/stridist-api-client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import InviteCodesService from "../../services/InviteCodesService";
import { useNavigate } from "react-router-dom";
import {
  CLIENT_GROUP_ROUTE,
  CLIENT_PROGRAMS_PROGRAM_ROUTE,
  HOME_ROUTE,
} from "../../routes/routes";
import { NAVBAR_GROUP_LIST_QUERY_KEY } from "../groups/GroupNavItemList";
import { INVALIDATE_KEYS_ON_ACCEPT_PROGRAM_INVITE_CODE } from "../client-programs-code/ProgramInvitePreview";

interface ClientAcceptProgramGroupCodeScreenProps {
  invite: ValidatedInviteCodeDto;
}

const ClientAcceptProgramGroupCodeScreen = (
  props: ClientAcceptProgramGroupCodeScreenProps,
) => {
  const { invite, ...other } = props;

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { mutate: acceptInviteCode, isPending } = useMutation({
    mutationKey: ["accept-invite-code"],
    mutationFn: InviteCodesService.processInviteCode,
    onSuccess: (data: ProcessInviteCodeDto) => {
      const slug = data.groupSlug ?? data.programSlug;

      if (!data.groupSlug) {
        queryClient.invalidateQueries({
          queryKey: [NAVBAR_GROUP_LIST_QUERY_KEY],
          exact: false,
        });
      } else if (!data.programSlug) {
        INVALIDATE_KEYS_ON_ACCEPT_PROGRAM_INVITE_CODE.forEach((queryKey) => {
          queryClient.invalidateQueries({
            queryKey: [queryKey],
            exact: false,
          });
        });
      }

      const navigateTo = slug
        ? data.groupSlug != null
          ? CLIENT_GROUP_ROUTE.replace(":slug", slug)
          : CLIENT_PROGRAMS_PROGRAM_ROUTE.replace(":program", slug)
        : HOME_ROUTE;

      navigate(navigateTo);
    },
  });

  const handleAcceptInviteCode = React.useCallback(() => {
    acceptInviteCode({ code: invite.code });
  }, [acceptInviteCode, invite.code]);

  if (!invite.groupInfo && !invite.programInfo) {
    throw new Error("Invite must be for a group or a program");
  }

  return (
    <ClientDetailedLinkInviteScreenContent
      invite={invite}
      onButtonClick={handleAcceptInviteCode}
      isLoading={isPending}
      {...other}
    />
  );
};

export default ClientAcceptProgramGroupCodeScreen;
